import { Badge } from "@/shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { AlertCircle, Sparkles } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { formatDistanceToNow } from "date-fns";
import type { Asset } from "@/features/assets/hooks/usePropertyAssetRegistry";

// TODO: temporarily hidden — re-enable when AI assessment is ready to ship.
const SHOW_AI_ASSESSMENT = false;

function confidencePillClass(v: number | null | undefined) {
  if (v == null) return null;
  if (v >= 0.6)  return 'bg-status-success/10 text-status-success border-status-success/30';
  if (v >= 0.4)  return 'bg-status-warning/10 text-status-warning border-status-warning/30';
  return 'bg-status-critical/10 text-status-critical border-status-critical/30';
}

export function AIAssessmentCard({ asset }: { asset: Asset }) {
  const hasAssessment =
    SHOW_AI_ASSESSMENT && (
      asset.ai_suggested_condition ||
      asset.ai_suggested_rul_months != null ||
      (Array.isArray(asset.ai_recommendations) && asset.ai_recommendations.length > 0) ||
      asset.ai_completed_at ||
      asset.ai_failure_reason
    );

  if (!hasAssessment) return null;

  const recs = Array.isArray(asset.ai_recommendations)
    ? (asset.ai_recommendations as unknown[])
    : [];

  return (
    <Card className="animate-fade-in border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            AI Assessment
          </CardTitle>
          <div className="flex items-center gap-2">
            {asset.ai_confidence != null && confidencePillClass(asset.ai_confidence) && (
              <Badge className={cn("text-xs", confidencePillClass(asset.ai_confidence)!)} variant="outline">
                AI {Math.round(asset.ai_confidence * 100)}%
              </Badge>
            )}
            {asset.ocr_confidence != null && confidencePillClass(asset.ocr_confidence) && (
              <Badge className={cn("text-xs", confidencePillClass(asset.ocr_confidence)!)} variant="outline">
                OCR {Math.round(asset.ocr_confidence * 100)}%
              </Badge>
            )}
          </div>
        </div>
        {asset.ai_completed_at && (
          <p className="text-xs text-muted-foreground mt-1">
            Analyzed {formatDistanceToNow(new Date(asset.ai_completed_at), { addSuffix: true })}
            {asset.ai_model_used ? ` · ${asset.ai_model_used}` : ''}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {asset.ai_failure_reason && (
          <div className="rounded-md border border-status-critical/30 bg-status-critical/5 p-3 text-xs text-status-critical">
            <div className="font-semibold mb-1 flex items-center gap-1">
              <AlertCircle className="h-3.5 w-3.5" /> AI processing failed
            </div>
            <div className="text-status-critical/80 break-words">{asset.ai_failure_reason}</div>
          </div>
        )}

        {(asset.ai_suggested_condition || asset.ai_suggested_rul_months != null) && (
          <div className="grid grid-cols-2 gap-2">
            {asset.ai_suggested_condition && (
              <div className="rounded-md border border-border/40 bg-muted/20 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Suggested condition</div>
                <div className="text-sm font-semibold capitalize">{asset.ai_suggested_condition}</div>
              </div>
            )}
            {asset.ai_suggested_rul_months != null && (
              <div className="rounded-md border border-border/40 bg-muted/20 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Remaining useful life</div>
                <div className="text-sm font-semibold">
                  {asset.ai_suggested_rul_months >= 12
                    ? `${Math.floor(asset.ai_suggested_rul_months / 12)}y ${asset.ai_suggested_rul_months % 12}m`
                    : `${asset.ai_suggested_rul_months}m`}
                </div>
              </div>
            )}
          </div>
        )}

        {recs.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-2">
              Recommendations
            </div>
            <ul className="space-y-2">
              {recs.map((r, i) => {
                if (typeof r === 'string') {
                  return (
                    <li key={i} className="text-sm flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span className="break-words">{r}</span>
                    </li>
                  );
                }
                const rec = r as { title?: string; detail?: string; urgency?: string };
                const urgencyClass =
                  rec.urgency === 'HIGH'
                    ? 'bg-status-critical/10 text-status-critical border-status-critical/30'
                    : rec.urgency === 'MEDIUM'
                      ? 'bg-status-warning/10 text-status-warning border-status-warning/30'
                      : 'bg-status-info/10 text-status-info border-status-info/30';
                return (
                  <li key={i} className="rounded-md border border-border/40 bg-muted/20 p-3">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="text-sm font-semibold break-words">{rec.title ?? '(untitled)'}</div>
                      {rec.urgency && (
                        <Badge className={cn("text-[10px] flex-shrink-0", urgencyClass)} variant="outline">
                          {rec.urgency}
                        </Badge>
                      )}
                    </div>
                    {rec.detail && (
                      <p className="text-xs text-muted-foreground break-words">{rec.detail}</p>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
